import {IncomingHttpHeaders} from 'http';
import {TFunction} from 'i18next';
import {ActionType, actions} from './action';
import {DEFAULT_RATING} from '../constant';
import {Page} from '../constant/page';
import {
  IUserAgent,
  IFetchInfoItem,
  IFetchStatus,
  IToast,
  ILayoutProps,
  INotice,
  ILanguage,
  IUserCashInfo,
  IWebToAppBanner,
  ITheme,
} from '../type';
import {PAlert} from '../type/alert';
import {ApiResultCode} from '../util/api/apiError';
import {
  createReducer,
  ReducerActionHelper,
  createBasicReducerHandlers,
} from '../util/redux';

export interface IStateCommon {
  pAlerts: PAlert[];
  toasts: IToast[];
  isHideAppDownloadBanners: {[key in Page]?: boolean};
  fetchInfo: {
    fetchStatusMap: IFetchInfoItem<IFetchStatus>;
    isSlowMap: IFetchInfoItem<boolean>;
    totalCountMap: IFetchInfoItem<number>;
    nextPagingMap: IFetchInfoItem<string | number>;
    isEndMap: IFetchInfoItem<boolean>;
    // 한 번이라도 fetch 완료하면 true
    // 페이징 시에는 첫 번째 페이지의 fetch가 시작되면 false로 변경되고 끝나면 true로 변경된다
    isFirstFetchedMap: IFetchInfoItem<boolean>;
    errorCodeMap: IFetchInfoItem<number>;
    errorMessageMap: IFetchInfoItem<string>;
  };
  isPageLoadingSlow: boolean;
  isPageLoading: boolean;
  showFullLoadingCount: number;
  userAgent?: IUserAgent;
  isAppMounted: boolean;
  initRandom: number;
  ratingInfo: {
    showModal: boolean;
  };
  currentRating: number;
  layoutProps: ILayoutProps;
  hasNewInfo: {
    [Page.Home]: boolean;
  };
  lang?: ILanguage;
  headers?: IncomingHttpHeaders;
  tFunction?: TFunction;
  userTotalCash?: number;
  userCashInfo?: IUserCashInfo;
  noticeList: INotice[];
  noticeTitleKey: string;
  contentLikeMap: {[id: number]: boolean | undefined};
  isOnLogout: boolean; // 로그 아웃 호출 후, 메인페이지로 이동할때까지 true
  isLoadedBannerSdk: {
    moment: boolean;
    native: boolean;
  };
  webToAppBanner: {
    viewer?: IWebToAppBanner;
    normal?: IWebToAppBanner;
  };
  theme?: ITheme;
}

const INITIAL_STATE: IStateCommon = {
  pAlerts: [],
  toasts: [],
  isHideAppDownloadBanners: {},
  fetchInfo: {
    fetchStatusMap: {},
    isSlowMap: {},
    totalCountMap: {},
    nextPagingMap: {},
    isEndMap: {},
    isFirstFetchedMap: {},
    errorCodeMap: {},
    errorMessageMap: {},
  },
  isPageLoadingSlow: false,
  isPageLoading: true,
  showFullLoadingCount: 0,
  isAppMounted: false,
  initRandom: 0,
  ratingInfo: {
    showModal: false,
  },
  currentRating: DEFAULT_RATING,
  layoutProps: {page: Page.Home},
  hasNewInfo: {
    [Page.Home]: false,
  },
  lang: undefined,
  theme: undefined,
  noticeList: [],
  noticeTitleKey: '',
  contentLikeMap: {},
  isOnLogout: false,
  isLoadedBannerSdk: {
    moment: false,
    native: false,
  },
  webToAppBanner: {
    viewer: undefined,
    normal: undefined,
  },
};

export default createReducer<IStateCommon, ReducerActionHelper<typeof actions>>(
  INITIAL_STATE,
  {
    ...createBasicReducerHandlers({
      setValueActionType: ActionType.SetValue,
      setPropertyValuesActionType: ActionType.SetPropertyValues,
    }),
    [ActionType.AddPAlert]: (state, action) => {
      const alert = action.payload.data;
      const prevAlert =
        alert.groupId &&
        state.pAlerts.find(item => item.groupId === alert.groupId);
      if (prevAlert) {
        prevAlert.callback = getMergedFunction(
          prevAlert.callback,
          alert.callback
        );
        prevAlert.callbackSecond = getMergedFunction(
          prevAlert.callbackSecond,
          alert.callbackSecond
        );
        prevAlert.onRequestClose = getMergedFunction(
          prevAlert.onRequestClose,
          alert.onRequestClose
        );
      } else {
        state.pAlerts.push(action.payload.data);
      }
    },
    [ActionType.RemovePAlert]: state =>
      state.pAlerts.length && state.pAlerts.splice(0, 1),
    [ActionType.RemoveAllPAlerts]: state => (state.pAlerts = []),
    [ActionType.RemoveAllPAlertsForRouting]: state =>
      (state.pAlerts = state.pAlerts.filter(p => p.isAliveWhenRouting)),
    [ActionType.ChangePAlertConfirmState]: (state, action) => {
      const {groupId, disabled} = action.payload;
      const targetAlert = state.pAlerts.find(item => item.groupId === groupId);
      if (targetAlert) {
        targetAlert.confirmDisabled = disabled;
      }
    },
    [ActionType.AddToast]: (state, action) => {
      const newToast = action.payload.data;
      if (newToast.isShowEvery) {
        state.toasts = [newToast];
      } else if (
        !state.toasts.find(item => item.toastId === newToast.toastId)
      ) {
        state.toasts.push(newToast);
      }
    },
    [ActionType.RemoveToast]: state =>
      state.toasts.length && state.toasts.splice(0, 1),
    [ActionType.SetFetchStatus]: (state, action) => {
      const {
        actionType,
        fetchKey,
        status,
        totalCount,
        nextPaging,
        isEnd,
        isFirstFetch,
        resultCode,
        resultMessage,
      } = action.payload;
      (state.fetchInfo.fetchStatusMap[actionType] ??= {})[fetchKey] = status;
      if (isFirstFetch) {
        (state.fetchInfo.isFirstFetchedMap[actionType] ??= {})[fetchKey] =
          status !== 'request';
      }

      if (status !== 'request') {
        if (state.fetchInfo.isSlowMap[actionType]) {
          (state.fetchInfo.isSlowMap[actionType] ??= {})[fetchKey] = false;
        }
        if (totalCount !== undefined) {
          (state.fetchInfo.totalCountMap[actionType] ??= {})[fetchKey] =
            totalCount;
        }
        if (nextPaging !== undefined) {
          (state.fetchInfo.nextPagingMap[actionType] ??= {})[fetchKey] =
            nextPaging;
        }
        if (isEnd !== undefined) {
          (state.fetchInfo.isEndMap[actionType] ??= {})[fetchKey] = isEnd;
        }
        const isSaveError =
          resultCode !== undefined && resultCode !== ApiResultCode.Success;
        if (isSaveError) {
          if (resultCode) {
            (state.fetchInfo.errorCodeMap[actionType] ??= {})[fetchKey] =
              resultCode;
          }

          if (resultMessage) {
            (state.fetchInfo.errorMessageMap[actionType] ??= {})[fetchKey] =
              resultMessage;
          }
        }
      }
    },
    [ActionType.SetIsSlow]: (state, action) => {
      const {actionType, fetchKey, isSlow} = action.payload;
      (state.fetchInfo.isSlowMap[actionType] ??= {})[fetchKey] = isSlow;
    },
    [ActionType.SetIsFirstFetched]: (state, action) => {
      const {actionType, fetchKey, isFirstFetched} = action.payload;
      (state.fetchInfo.isFirstFetchedMap[actionType] ??= {})[fetchKey] =
        isFirstFetched;
    },
    [ActionType.SetUserAgent]: (state, action) => {
      if (!state.userAgent) {
        state.userAgent = action.payload.userAgent;
      }
    },
    [ActionType.SetIsAppMounted]: (state, action) => {
      const {isAppMounted} = action.payload;
      state.isAppMounted = isAppMounted;
      if (isAppMounted) {
        state.isPageLoading = false;
      }
    },
    [ActionType.RemoveFetchError]: (state, action) => {
      const {actionType, fetchKey} = action.payload;
      if (state.fetchInfo.errorCodeMap[actionType]) {
        delete state.fetchInfo.errorCodeMap[actionType][fetchKey];
      }
      if (state.fetchInfo.errorMessageMap[actionType]) {
        delete state.fetchInfo.errorMessageMap[actionType][fetchKey];
      }
    },
    [ActionType.SetIsHideAppDownloadBanners]: (state, action) => {
      const {page, value} = action.payload;
      state.isHideAppDownloadBanners[page] = value;
    },
    [ActionType.SetFullLoading]: (state, action) => {
      const {isShow} = action.payload;
      state.showFullLoadingCount = isShow
        ? state.showFullLoadingCount + 1
        : Math.max(state.showFullLoadingCount - 1, 0);
    },
    [ActionType.AddNoticeList]: (state, action) => {
      const {noticeList, isFirst} = action.payload;
      if (isFirst) state.noticeList = [];
      state.noticeList.push(...noticeList);
    },
    [ActionType.SetContentLike]: (state, action) => {
      const {id, isLike} = action.payload;
      state.contentLikeMap[id] = isLike;
    },
    [ActionType.SetContentLikes]: (state, action) => {
      const {likeIds, isLike} = action.payload;
      likeIds.forEach(id => {
        state.contentLikeMap[id] = isLike;
      });
    },
    [ActionType.SetValuesWhenRoutingFinished]: state => {
      state.isOnLogout = false;
      state.isPageLoading = false;
      state.isPageLoadingSlow = false;
    },
    [ActionType.SetUserCashInfo]: (state, action) => {
      const {userCashInfo, totalCash} = action.payload;
      state.userTotalCash = totalCash;
      state.userCashInfo = userCashInfo;
    },
    [ActionType.SetWebToAppBanner]: (state, action) => {
      const {banner, isViewerPage} = action.payload;
      if (isViewerPage) {
        state.webToAppBanner.viewer = banner;
      } else {
        state.webToAppBanner.normal = banner;
      }
    },
    [ActionType.DecreaseTotalCount]: (state, action) => {
      const {actionType, fetchKey} = action.payload;
      const {totalCountMap} = state.fetchInfo;
      if (totalCountMap[actionType]?.[fetchKey]) {
        --totalCountMap[actionType][fetchKey];
      }
    },
    [ActionType.RemoveFetchStatus]: (state, action) => {
      const {actionList} = action.payload;
      const {fetchInfo} = state;
      actionList.forEach(({actionType, fetchKey}) => {
        if (fetchKey) {
          delete fetchInfo.fetchStatusMap[actionType][fetchKey];
        } else {
          delete fetchInfo.fetchStatusMap[actionType];
        }
      });
    },
  }
);

function getMergedFunction(prev?: () => void, next?: () => void) {
  if (!prev || !next) {
    return undefined;
  }
  return () => {
    prev();
    next && next();
  };
}
